import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/lib/toast";
import { Bot, ChevronLeft, Copy, KeyRound, RefreshCw, Trash2 } from "lucide-react";

/** The command a teammate runs once to add this server to Claude Code. */
function claudeMcpAddCommand(key: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://yourdomain.com";
  return `claude mcp add --transport http footage ${origin}/mcp --header "Authorization: Bearer ${key}"`;
}

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
}

export default function AgentAccess() {
  const { user, loading } = useAuth({ redirectOnUnauthenticated: true });
  const [, navigate] = useLocation();
  const isAdmin = !!user && user.role === "admin";

  const [newName, setNewName] = useState("");
  const [createdKey, setCreatedKey] = useState<{ name: string; key: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    if (!loading && user && user.role !== "admin") navigate("/");
  }, [loading, user, navigate]);

  const { data: keys = [], refetch } = trpc.admin.listAgentKeys.useQuery(undefined, {
    enabled: isAdmin,
  });

  const createKey = trpc.admin.createAgentKey.useMutation({
    onSuccess: ({ key, record }) => {
      setCreatedKey({ name: record.name, key });
      setNewName("");
      refetch();
    },
    onError: (err) => toast.error(`Couldn't create key: ${err.message}`),
  });

  const revokeKey = trpc.admin.revokeAgentKey.useMutation({
    onSuccess: () => {
      toast.success("Key revoked. Its download links stop working too.");
      refetch();
    },
    onError: (err) => toast.error(`Couldn't revoke key: ${err.message}`),
  });

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed. Select the text and copy it yourself.");
    }
  }

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const active = keys.filter((k) => !k.revokedAt);
  const revoked = keys.filter((k) => k.revokedAt);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          <Bot className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Agent Access</h1>
            <p className="text-sm text-muted-foreground">
              API keys that let Claude Code search the library and download clips for video edits.
            </p>
          </div>
        </div>

        <div className="border border-border rounded-lg p-5 mb-6 bg-card space-y-2 text-sm">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            What agents can reach
          </h2>
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>Provider A-Roll, with transcripts and subtitles.</li>
            <li>
              Patient B-Roll only when the patient is <strong>Pre-Approved</strong> without Special
              Instructions. Patient names, folders and transcripts are never shown, and names are
              blanked out of descriptions.
            </li>
            <li>Never clips hidden for consent. Consent is checked again when each download starts.</li>
            <li>Every search and download is in the Audit Log under the key's name.</li>
          </ul>
        </div>

        <div className="border border-border rounded-lg p-5 mb-6 bg-card space-y-3">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
            Create Key
          </h2>
          <div className="flex items-end gap-2">
            <div className="space-y-1.5 flex-1">
              <Label htmlFor="agent-key-name">Name</Label>
              <Input
                id="agent-key-name"
                placeholder="e.g. Kyle's laptop, Edit bay"
                value={newName}
                maxLength={100}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newName.trim() && !createKey.isPending) {
                    createKey.mutate({ name: newName.trim() });
                  }
                }}
              />
            </div>
            <Button
              disabled={!newName.trim() || createKey.isPending}
              onClick={() => createKey.mutate({ name: newName.trim() })}
            >
              <KeyRound className="w-4 h-4 mr-1.5" />
              Create Key
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Make one key per person or machine, so you can revoke one without affecting the others.
          </p>

          {createdKey && (
            <div className="rounded-md border border-primary/40 bg-primary/5 p-4 space-y-3">
              <p className="text-sm font-medium">
                Key for &ldquo;{createdKey.name}&rdquo; created. Copy it now: it won't be shown again.
              </p>
              <div className="space-y-1.5">
                <Label>Run this once in a terminal to add the server to Claude Code</Label>
                <div className="flex gap-2">
                  <code className="flex-1 min-w-0 break-all rounded bg-muted px-2 py-1.5 font-mono text-xs">
                    {claudeMcpAddCommand(createdKey.key)}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={() => copy(claudeMcpAddCommand(createdKey.key))}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Key only</Label>
                <div className="flex gap-2">
                  <code className="flex-1 min-w-0 break-all rounded bg-muted px-2 py-1.5 font-mono text-xs">
                    {createdKey.key}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={() => copy(createdKey.key)}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </Button>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCreatedKey(null)}>
                Done
              </Button>
            </div>
          )}
        </div>

        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-muted/30 border-b border-border">
            <span className="text-sm font-semibold">
              {active.length} active key{active.length !== 1 ? "s" : ""}
            </span>
          </div>
          {keys.length === 0 ? (
            <p className="px-5 py-6 text-sm text-muted-foreground">No keys yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {[...active, ...revoked].map((k) => (
                <li key={k.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{k.name}</span>
                      <code className="text-xs text-muted-foreground font-mono">{k.keyPrefix}…</code>
                      {k.revokedAt && (
                        <span className="text-xs rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                          Revoked
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Created {formatDate(k.createdAt)}
                      {k.createdByName ? ` by ${k.createdByName}` : ""} · Last used{" "}
                      {formatDate(k.lastUsedAt)}
                      {k.revokedAt ? ` · Revoked ${formatDate(k.revokedAt)}` : ""}
                    </p>
                  </div>
                  {!k.revokedAt && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-destructive"
                      onClick={() => setRevokeTarget({ id: k.id, name: k.name })}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke &ldquo;{revokeTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Claude Code sessions using this key lose access immediately, and any download links it
              handed out stop working. This can't be undone; you'd create a new key instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (revokeTarget) revokeKey.mutate({ id: revokeTarget.id });
                setRevokeTarget(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
